import React, { useEffect, useState } from 'react'
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogTrigger } from '../../ui/alert-dialog'
import { AlertDialogCancel } from '@radix-ui/react-alert-dialog'
import { Button } from '../../ui/button'
import { isValid } from 'date-fns'
import vault from '../../../assets/images/vault.svg';
import { Label } from '../../ui/label'
import { Input } from '../../ui/input'
import { ArrowLeft, Check, Equal, X } from 'lucide-react'
import { Tablist, TablistItem } from '../../ui/tablist'

import { GenericCoin, GenericNote } from '../../elements/'

import { useDebounce } from '@uidotdev/usehooks'
import axios from '../../../lib/axios'
import useAuth from '../../../hooks/useAuth'
import { Toaster, toast } from 'react-hot-toast'
import { calculateTotal } from '../../../lib/coupure'
import NewCheckbox from '../../ui/new-checkbox'
import { getCookie, setCookie } from '../../../lib/cookies'
import { useTranslation } from 'react-i18next'

interface Props {
    selectedDate: Date,
    cn: Type | 'cn',
    onSave: (obj:AnnouncementKeepsafe) => void;
}


type Type = 'c' | 'n';

const NewKeepsafe : React.FC<Props> = ({selectedDate, cn, onSave}) => {
    const {auth} = useAuth()
    const {t} = useTranslation()

    const [coupure_input, setcoupure_input] = useState<CoupureInput[]>();
    const [selected_type, setselected_type] = useState<Type>((cn === 'c' || cn === 'cn') ? 'c' : 'n');
    const [barcode, setbarcode] = useState<string>('');
    const [barcode_error, setbarcode_error] = useState<'correct' | StateType>(null);

    const debouncedBarcode = useDebounce(barcode, 300)
    

    useEffect(()=>{
        if(debouncedBarcode !== '' && debouncedBarcode.substring(0,2) === 'BE') {
            const checkBarcode = async () => {
                try {
                    setbarcode_error('loading')
                    const { data } = await axios.post(`barcode/check`, {
                        token: auth.data?.token,
                        barcode: debouncedBarcode
                    })
                    if(data.errorcode === 0){
                        if(data.object.barcode.valid === true && data.object.barcode.remark === ''){
                            setbarcode_error('correct')
                        } else {
                            setbarcode_error('error')
                        }
                    }
                    else {
                        setbarcode_error('error')
                    }
                } catch (error) {
                    setbarcode_error('error')
                    console.log(error)
                }
            }
            checkBarcode()
        }
        else {
            setbarcode_error(null)
        }
    }, [debouncedBarcode])

    const onChangeInput = (coupure:CoupureType, qty:number) => {
        qty = Math.max(0, Math.round(qty))
        if(coupure_input) {
            let tmp = [...coupure_input]
            const existingIndex = coupure_input.findIndex(item => item.coupure === coupure);
            
            if(existingIndex !== -1){
                tmp[existingIndex] = {qty:qty, coupure:coupure}
            }
            else {
                tmp.push({qty:qty, coupure:coupure})
            }
            setcoupure_input(tmp);
        }
        else {
            setcoupure_input([{coupure:coupure, qty:qty}])
        }
    };

    const calculateSubtotal = (coupure:CoupureType) => {
        let total = 0;
        if(coupure_input) {
            const item = coupure_input.find(i=>i.coupure===coupure)
            if(item) {
                total = parseFloat(item.coupure) * item.qty
            }
        }
        return Math.round(total * 100)/100 || 0
    }

    const COINS: { value: CoupureType }[] = [
        { value: '2' }, { value: '1' }, { value: '0.5' }, { value: '0.2' },
        { value: '0.1' }, { value: '0.05' }, { value: '0.02' }, { value: '0.01' },
    ]
    const NOTES: { value: CoupureType }[] = [
        { value: '500' }, { value: '200' }, { value: '100' }, { value: '50' },
        { value: '20' }, { value: '10' }, { value: '5' },
    ]

    const CoinsInput = () => (
        <div className='flex flex-col gap-2'>
            {COINS.map(({ value }) => (
                <div className='grid grid-cols-[auto_auto_auto_auto_1fr] items-center gap-2' key={value}>
                    <Input onBlur={({currentTarget})=>{ onChangeInput(value, parseFloat(currentTarget.value)) }} defaultValue={coupure_input?.find(i => i.coupure === value)?.qty ?? 0} className='w-20' type='number' integerOnly min={0}/>
                    <span className='text-sm text-muted-foreground'>×</span>
                    <GenericCoin value={parseFloat(value)} currency="EUR" className="w-8 h-8"/>
                    <Equal className='w-5' strokeWidth={1}/>
                    <p className='font-bold text-right'>€ {calculateSubtotal(value)}</p>
                </div>
            ))}
            <div className='flex justify-end py-2 text-sm border-t'>
                <p>{t("components.o_announcement.o_newkeepsafe.total")}: <span className='font-medium'>€ {calculateTotal(coupure_input as CoupureInput[], 'c')}</span></p>
            </div>
        </div>
    )

    const NotesInput = () => (
        <div className='flex flex-col gap-2'>
            {NOTES.map(({ value }) => (
                <div className='grid grid-cols-[auto_auto_auto_auto_1fr] items-center gap-2' key={value}>
                    <Input onBlur={({currentTarget})=>{ onChangeInput(value, parseFloat(currentTarget.value)) }} defaultValue={coupure_input?.find(i => i.coupure === value)?.qty ?? 0} className='w-20' type='number' integerOnly min={0}/>
                    <span className='text-sm text-muted-foreground'>×</span>
                    <GenericNote value={parseFloat(value)} currency="EUR" className="w-16 h-8"/>
                    <Equal className='w-5' strokeWidth={1}/>
                    <p className='font-bold text-right'>€ {calculateSubtotal(value)}</p>
                </div>
            ))}
            <div className='flex justify-end py-2 text-sm border-t'>
                <p>{t("components.o_announcement.o_newkeepsafe.total")}: <span className='font-medium'>€ {calculateTotal(coupure_input as CoupureInput[], 'n')}</span></p>
            </div>
        </div>
    )

    const saveKeepsafe = () => {
        let object:AnnouncementKeepsafe = {keepsafe:barcode, detail:[]}

        if(object.keepsafe === ''){
            toast.error(t("components.o_announcement.o_newkeepsafe.toast_error"))
            return;
        }
        if(coupure_input){
            //Keepsafe aangemaakt
            
            console.log(object)
            console.log(coupure_input)
            

            object.detail.push({bag:barcode, coupures:coupure_input})

            if(selected_type === 'c'){
                //Keep all coins
                object.detail[0].coupures = object.detail[0].coupures.filter(i => parseFloat(i.coupure) <= 2)
            }
            else {
                //Keep all notes
                object.detail[0].coupures = object.detail[0].coupures.filter(i => parseFloat(i.coupure) > 2)
            }
        }
        else {
            //Moederkeepsafe aanmaken
        }
        onSave(object)
        setcoupure_input(undefined)
        setbarcode('')
    }

    type MethodKeepsafe = 'manual' | 'amount'
    //    const [method_keepsafe, setmethod_keepsafe] = useState<MethodKeepsafe | null>(getCookie('methodKeepsafe') ? getCookie('methodKeepsafe') as MethodKeepsafe : null);
    const [method_keepsafe, setmethod_keepsafe] = useState<MethodKeepsafe | null>('manual');
    const [remember_method, setremember_method] = useState<boolean>(true);

    const saveMethod = (method:MethodKeepsafe) => {
        if(remember_method){
            setCookie('methodKeepsafe', method)
        }
        setmethod_keepsafe(method)
    }

  return (
    <>
    <Toaster />
    <AlertDialog>
        <AlertDialogTrigger asChild>
            <Button variant='dark' disabled={!(isValid(selectedDate))} className='flex gap-2 mt-4 w-fit'><img className='h-6' src={vault} alt="vault"/> {t("components.o_announcement.o_newkeepsafe.button_new_keepsafe")}</Button>
        </AlertDialogTrigger>
        <AlertDialogContent className='max-h-[95vh] overflow-y-auto'>
            {method_keepsafe === null &&
            <div className='flex flex-col gap-4'>
                <div className='flex items-center justify-between'>
                    <h2 className='text-xl font-medium'>{t("components.o_announcement.o_newkeepsafe.keepsafe_question")}</h2>
                    <AlertDialogCancel asChild><button className='opacity-50 hover:opacity-100'><X /></button></AlertDialogCancel>
                </div>
                <div className='flex items-center gap-2'>
                    <NewCheckbox checked={remember_method} onCheckedChange={(v: boolean)=>{setremember_method(v)}} id='remember_ks'/>
                    <Label htmlFor='remember_ks'>{t("components.o_announcement.o_newkeepsafe.remember")}</Label>
                </div>
                <div className='flex gap-2'>
                    <Button onClick={()=>{saveMethod('manual')}}>{t("components.o_announcement.o_newkeepsafe.manual")}</Button>
                    <Button onClick={()=>{saveMethod('amount')}} variant='outline'>{t("components.o_announcement.o_newkeepsafe.loose")}</Button>
                </div>
            </div>
            }

            {method_keepsafe === 'manual' &&
            <>
            <div className='flex items-center justify-between'>
                <div className='relative overflow-hidden group'>
                    <div className='flex items-center gap-1 duration-100 -translate-x-6 cursor-pointer group-hover:translate-x-0' onClick={()=>{setmethod_keepsafe(null)}}>
                        <ArrowLeft className='w-5'/>
                        <h2 className='text-xl font-medium'>{t("components.o_announcement.o_newkeepsafe.title")}</h2>
                    </div>
                </div>
                <AlertDialogCancel asChild><button className='opacity-50 hover:opacity-100'><X /></button></AlertDialogCancel>
            </div>

            <div>
                <Label htmlFor='barcode'>{t("components.o_announcement.o_newkeepsafe.label_barcode")}<span className='ml-1 text-primary'>*</span></Label>
                <Input endEnhancer={
                <>
                    {barcode_error === 'correct' && <div className='flex items-center justify-center w-5 h-5 bg-green-400 rounded-full'><Check className='w-3 text-white'/></div>}
                    {barcode_error === 'error' && <div className='flex items-center justify-center w-5 h-5 bg-red-400 rounded-full'><X className='w-3 text-white'/></div>}
                </>} onChange={({currentTarget})=>setbarcode(currentTarget.value)} id='barcode'/>
                {barcode_error === 'error' && <p className='text-sm text-red-600'>{t("components.o_announcement.o_newkeepsafe.barcode_error")}</p>}
            </div>
            
            {cn === 'cn' && 
            <Tablist value={selected_type} onValueChange={(v)=>{setselected_type(v as Type)}}>
                <TablistItem value='c'>{t("components.o_announcement.o_newkeepsafe.coins")}</TablistItem>
                <TablistItem value='n'>{t("components.o_announcement.o_newkeepsafe.notes")}</TablistItem>
            </Tablist>
            }

            {selected_type === 'c' && <CoinsInput />}
            {selected_type === 'n' && <NotesInput />}

            <div>
                <AlertDialogAction disabled={barcode === ''} onClick={()=>{saveKeepsafe()}}>{t("components.o_announcement.o_newkeepsafe.button_save_keepsafe")}</AlertDialogAction>
                <Button onClick={()=>{setcoupure_input(undefined)}} variant='ghost'>{t("components.o_announcement.o_newkeepsafe.button_clear_form")}</Button>
            </div>
            </>
            }

            {method_keepsafe === 'amount' &&
            <>
            <div className='flex items-center justify-between'>
                <div className='relative overflow-hidden group'>
                    <div className='flex items-center gap-1 duration-100 -translate-x-6 cursor-pointer group-hover:translate-x-0' onClick={()=>{setmethod_keepsafe(null)}}>
                        <ArrowLeft className='w-5'/>
                        <h2 className='text-xl font-medium'>{t("components.o_announcement.o_newkeepsafe.title")}</h2>
                    </div>
                </div>
                <AlertDialogCancel asChild><button className='opacity-50 hover:opacity-100'><X /></button></AlertDialogCancel>
            </div>
            <div>
                <Label htmlFor='barcode'>{t("components.o_announcement.o_newkeepsafe.label_barcode")}<span className='ml-1 text-primary'>*</span></Label>
                <Input endEnhancer={
                <>
                    {barcode_error === 'correct' && <div className='flex items-center justify-center w-5 h-5 bg-green-400 rounded-full'><Check className='w-3 text-white'/></div>}
                    {barcode_error === 'error' && <div className='flex items-center justify-center w-5 h-5 bg-red-400 rounded-full'><X className='w-3 text-white'/></div>}
                </>} onChange={({currentTarget})=>setbarcode(currentTarget.value)} id='barcode'/>
                {barcode_error === 'error' && <p className='text-sm text-red-600'>{t("components.o_announcement.o_newkeepsafe.barcode_error")}</p>}
            </div>
            <div>
                <Label htmlFor='amount'>{t("components.o_announcement.o_newkeepsafe.total_amount")}</Label>
                <Input id='amount' type='number'/>
            </div>
            <div>
                <AlertDialogAction disabled={barcode === ''} onClick={()=>{saveKeepsafe()}}>{t("components.o_announcement.o_newkeepsafe.button_save_keepsafe")}</AlertDialogAction>
            </div>
            </>
            }
            
        </AlertDialogContent>
    </AlertDialog>
    </>
  )
}

export default NewKeepsafe
